import { Button } from "@/components/ui/button";
import { Server, Zap, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import Footer from "@/components/Footer";
import logoSrc from "@/assets/arqera-logo-optimized.png";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function AdvancedSolutions() {
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="bg-deep-navy text-foreground">
      <SEO 
        title="Advanced AI Infrastructure Solutions — ArqeraAI | Enterprise Systems"
        description="Expert AI infrastructure consulting for enterprises. Custom AI systems, multi-agent orchestration, and intelligent automation solutions. Scale your AI operations with confidence."
        canonical="https://arqeraai.com/advanced-solutions"
      />
      {/* Simple Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-deep-navy/90 backdrop-blur-md border-b-2 border-cyan/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src={logoSrc} alt="ArqeraAI Logo" className="h-10 w-10" />
            <span className="text-base font-bold tracking-wider text-foreground uppercase">
              ARQERA.AI
            </span>
          </Link>
          <Button
            asChild
            className="bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200"
          >
            <a
              href="https://calendly.com/arqeraai-strategy-call/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule Consultation
            </a>
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-deep-navy pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-cyan/20 rounded-full blur-3xl float-animation" />
          <div className="absolute top-40 right-20 w-80 h-80 bg-cyan/15 rounded-full blur-3xl float-slow" />
          <div className="absolute bottom-20 right-40 w-72 h-72 bg-accent-teal/10 rounded-full blur-3xl float-animation" style={{ animationDelay: "2s" }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center fade-in-up">
          <h1 className="text-5xl md:text-7xl font-bold text-foreground leading-tight mb-6">
            Private AI. Infinite Scale.
          </h1>
          <p className="text-xl md:text-2xl text-light-gray/90 leading-relaxed mb-10 max-w-3xl mx-auto">
            Own your infrastructure. Automate your operations. Build your unfair advantage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan/50 px-8 py-6 text-lg"
            >
              <a
                href="https://calendly.com/arqeraai-strategy-call/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Schedule Infrastructure Consultation
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-cyan text-foreground hover:bg-cyan hover:text-white hover:scale-105 transition-all duration-200 px-8 py-6 text-lg"
            >
              <Link to="/">Back to Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Own Your Stack */}
      <section className="py-20 md:py-32 px-6 bg-medium-navy">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Why Own Your AI Infrastructure?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div className="bg-dark-card rounded-xl border border-cyan/10 p-10 hover:border-cyan/30 hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Stop Renting Your Intelligence
              </h3>
              <p className="text-lg text-light-gray/80">
                Your AI models. Your data. Your rules. Never worry about vendor lock-in or price hikes. You own the entire stack—from GPUs to governance.
              </p>
            </div>
            <div className="bg-dark-card rounded-xl border border-cyan/10 p-10 hover:border-cyan/30 hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Build Your Moat
              </h3>
              <p className="text-lg text-light-gray/80">
                Custom LLMs trained on your data. Proprietary workflows. Infrastructure competitors can't replicate. This is how you stay ahead.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Pillars */}
      <section className="py-20 md:py-32 px-6 bg-deep-navy">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 rounded-full border border-cyan/30 bg-cyan/5 mb-6 text-xs text-cyan font-bold uppercase tracking-wider">
              Our Approach
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Your Infrastructure + Your Automation
            </h2>
            <p className="text-lg md:text-xl text-light-gray/80 max-w-3xl mx-auto">
              Built to scale. Built to last.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Pillar 1 */}
            <div className="bg-dark-card rounded-xl border border-cyan/10 p-10 hover:border-cyan/30 hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <div className="w-16 h-16 rounded-lg bg-cyan/10 flex items-center justify-center mb-6">
                <Server className="w-8 h-8 text-cyan" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                We Design Your AI Stack
              </h3>
              <p className="text-base text-light-gray/80 mb-6">
                Custom GPU clusters. Private LLM models. Secure data pipelines. Governance frameworks that match your compliance needs. Fully managed or hybrid—your choice.
              </p>
              <h4 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">
                What's Included:
              </h4>
              <ul className="space-y-2">
                {[
                  "Infrastructure architecture & deployment",
                  "Model fine-tuning & optimization",
                  "Security & compliance (HIPAA, SOC2, GDPR)",
                  "24/7 monitoring & SLA guarantees (99.9% uptime)",
                  "Cost optimization & scaling",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                    <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pillar 2 */}
            <div className="bg-dark-card rounded-xl border border-cyan/10 p-10 hover:border-cyan/30 hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <div className="w-16 h-16 rounded-lg bg-cyan/10 flex items-center justify-center mb-6">
                <Zap className="w-8 h-8 text-cyan" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Autonomous Operations
              </h3>
              <p className="text-base text-light-gray/80 mb-6">
                AI agents that run your business. Custom workflows for onboarding, sales, CRM intelligence, analytics, and more. Scale without adding headcount.
              </p>
              <h4 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">
                What's Included:
              </h4>
              <ul className="space-y-2">
                {[
                  "Agent architecture & design",
                  "Custom workflow automation",
                  "Integration with your tools (Salesforce, HubSpot, etc.)",
                  "Performance optimization & monitoring",
                  "Continuous improvement & scaling",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                    <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 md:py-32 px-6 bg-medium-navy">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 rounded-full border border-cyan/30 bg-cyan/5 mb-6 text-xs text-cyan font-bold uppercase tracking-wider">
              Results
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Proven Infrastructure. Proven ROI.
            </h2>
            <p className="text-lg md:text-xl text-light-gray/80 max-w-3xl mx-auto">
              Trusted by founders and enterprises building at scale.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { stat: "99.9%", label: "Uptime", description: "Always on. Always optimized. Infrastructure you can trust." },
              { stat: "60%", label: "Cost Reduction", description: "vs. traditional cloud providers. More control. Less expense." },
              { stat: "10x", label: "Faster Execution", description: "Custom AI infrastructure vs. off-the-shelf solutions." },
            ].map((metric, i) => (
              <div key={i} className="bg-dark-card rounded-xl border border-cyan/10 p-8 text-center">
                <div className="text-5xl font-bold text-cyan mb-2">{metric.stat}</div>
                <div className="text-xl font-semibold text-foreground mb-3">{metric.label}</div>
                <p className="text-sm text-light-gray/80">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32 px-6 bg-deep-navy">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Common Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: "How long does implementation take?",
                a: "Typically 4-8 weeks depending on scope and complexity. We custom-tailor timelines based on your infrastructure needs and existing systems. Faster implementations are possible for straightforward setups.",
              },
              {
                q: "What's the total cost?",
                a: "Pricing varies significantly based on infrastructure scale, model customization, agent complexity, and ongoing management. We provide custom quotes after understanding your requirements. No hidden fees.",
              },
              {
                q: "Can you integrate with our existing tools?",
                a: "Absolutely. We build around your current stack—Salesforce, HubSpot, Slack, custom APIs, data warehouses, etc. Our infrastructure is designed to be your central nervous system, not a replacement.",
              },
              {
                q: "What about security and compliance?",
                a: "Complete control and visibility. SOC2, HIPAA, GDPR compliance available. We architect with security-first principles. Your infrastructure. Your compliance framework. Your rules.",
              },
              {
                q: "How do you handle scaling?",
                a: "Infrastructure scales automatically as your business grows. We monitor, optimize, and adjust in real-time. You never worry about capacity. We handle it.",
              },
              {
                q: "What if we want to migrate later?",
                a: "No lock-in. Your data is yours. Your infrastructure is yours. If you ever want to move, we'll support the transition. We believe in transparency and control.",
              },
            ].map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-dark-card rounded-xl border border-cyan/10 px-6">
                <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-cyan">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-light-gray/80 pt-2">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 md:py-32 px-6 bg-medium-navy overflow-hidden">
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan/10 rounded-full float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Ready to Build Your Unfair Advantage?
          </h2>
          <p className="text-lg md:text-xl text-light-gray/80 mb-10">
            Let's design the infrastructure that powers your growth.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan/50 px-10 py-6 text-lg mb-6"
          >
            <a
              href="https://calendly.com/arqeraai-strategy-call/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule Infrastructure Consultation
            </a>
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-medium-gray">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-cyan" />
              <span>Custom infrastructure proposal</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-cyan" />
              <span>Free architecture audit</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-cyan" />
              <span>No commitment</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
